import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  Grid2,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';

import CoachingSchedule from './CoachingSchedule';
import ServiceStatus from './ServiceStatus';
import PersonalInfo from './PersonalInfo';
import GeneralSettings from './GeneralSettings';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store';
import { UserStatus } from '../../../core/constant/constant';

enum VIEWS {
  COACHING_SCHEDULE,
  SERVICE_STATUS,
  PERSONAL_INFO,
  GENERAL_SETTINGS
}

interface ViewMenuItem {
  id: VIEWS;
  label: string;
  icon: React.ReactNode;
}
const Statuses = [UserStatus.COMMUNITY_MEMBER, UserStatus.PENDING_MATCHING];
const ActiveCoachingStatuses = [UserStatus.ACTIVE_COACHING, UserStatus.IDLE_COACHING, UserStatus.EXITED_BY_ADMIN, UserStatus.EXITED_BY_REQUEST, UserStatus.EXITED_BY_STOP, UserStatus.EXITED, UserStatus.AWAY, UserStatus.AWAY_BUDGET_LIMIT, UserStatus.AWAY_TEXT_LIMIT,];
const AccountSettings: React.FC = () => {
  const [activeView, setActiveView] = useState<VIEWS>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription);
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const isMembershipActive = ActiveCoachingStatuses.includes(basicInfo.currentStatus as UserStatus) || (memberSubscription.amount !== 0) && !Statuses.includes(basicInfo.currentStatus as UserStatus)
  const menuItems: ViewMenuItem[] = [
    {
      id: VIEWS.COACHING_SCHEDULE,
      label: 'Coaching Schedule',
      icon: <CalendarMonthIcon fontSize="small" sx={{ mr: 1 }} />
    },
    {
      id: VIEWS.SERVICE_STATUS,
      label: 'Service Status',
      icon: <ToggleOnIcon fontSize="small" sx={{ mr: 1 }} />
    },
    {
      id: VIEWS.PERSONAL_INFO,
      label: 'Profile',
      icon: <PersonIcon fontSize="small" sx={{ mr: 1 }} />
    },
    {
      id: VIEWS.GENERAL_SETTINGS,
      label: 'General',
      icon: <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
    }
  ];

  const handleMenuClick = (view: VIEWS): void => {
    setActiveView(view);
  };

  const handleClose = () => {
    console.log('Close button clicked');
  };

  const determineActiveClass = (currentView: VIEWS) => ({
    color: activeView === currentView ? theme.palette.primary.main : theme.palette.text.secondary,
    fontWeight: activeView === currentView ? 500 : 400,
    bgcolor: activeView === currentView ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
    padding: '10px 16px',
    borderRadius: '4px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: '0.9rem',
    width: '100%',
    '&:hover': {
      bgcolor: activeView === currentView
        ? 'rgba(99, 102, 241, 0.12)'
        : 'rgba(99, 102, 241, 0.04)'
    }
  });

  const determineViewToRender = () => {
    switch (activeView) {
      case VIEWS.COACHING_SCHEDULE:
        return <CoachingSchedule />;
      case VIEWS.SERVICE_STATUS:
        return <ServiceStatus />;
      case VIEWS.PERSONAL_INFO:
        return <PersonalInfo />;
      case VIEWS.GENERAL_SETTINGS:
        return <GeneralSettings />;
      default:
        return <CoachingSchedule />;;
    }
  };

  const renderViewTitle = () => {
    const activeItem = menuItems.find(item => item.id === activeView);
    return activeItem?.label || '';
  };
  useEffect(() => {
    if (!isMembershipActive) {
      setActiveView(VIEWS.PERSONAL_INFO)
    } else {
      setActiveView(VIEWS.COACHING_SCHEDULE)
    }
    return () => { }
  }, [isMembershipActive])
 

  const accountSideMenu = menuItems.slice(isMembershipActive ? 0 : 2, 4)
  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 2, pt: 0, mt: 0, bgcolor: '#F9FAFB' }}>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              height: '100%',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Account Settings
              </Typography>
            </Box>

            <Divider />

            <Stack spacing={1} sx={{ p: 2 }}>
              {accountSideMenu.map((item) => (
                <Button
                  key={item.id}
                  startIcon={item.icon}
                  onClick={() => handleMenuClick(item.id)}
                  sx={determineActiveClass(item.id)}
                  disableRipple
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Paper>
        </Grid2>

        {/* Main Content Area */}
        <Grid2 size={{ xs: 12, md: 9 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" fontWeight={500}>
                {renderViewTitle()}
              </Typography>

              {isMobile && (
                <IconButton
                  size="small"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>

            {determineViewToRender()}
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default AccountSettings;